import { useState, useEffect, useMemo, useCallback } from "react";
import { Table, CombinedTable } from "@/types/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Users, Check, Layers, ChevronRight, Minus, Plus } from "lucide-react";
import {
  generateConsecutiveCombinations,
  TableCombination,
} from "@/lib/tableCombinationUtils";

interface AutoCombineTablesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tables: Table[];
  combinedTables: CombinedTable[];
  onCreateCombinations: (combinations: TableCombination[]) => Promise<void>;
}

type Step = "selectTables" | "preview";

export function AutoCombineTablesDialog({
  open,
  onOpenChange,
  tables,
  combinedTables,
  onCreateCombinations,
}: AutoCombineTablesDialogProps) {
  const [step, setStep] = useState<Step>("selectTables");
  const [selectedTableIds, setSelectedTableIds] = useState<Set<string>>(new Set());
  const [isCreating, setIsCreating] = useState(false);
  const [selectedCombinations, setSelectedCombinations] = useState<Set<number>>(new Set());
  const [effectiveCapacities, setEffectiveCapacities] = useState<Map<number, number>>(new Map());

  // Get active tables only
  const activeTables = useMemo(() => tables.filter((t) => t.isActive), [tables]);

  // Sort tables by name for manual selection
  const sortedActiveTables = useMemo(
    () => [...activeTables].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })),
    [activeTables]
  );

  // Get selected tables for manual mode (sorted by name)
  const manualSelectedTables = useMemo(() => {
    return sortedActiveTables.filter((t) => selectedTableIds.has(t.id));
  }, [sortedActiveTables, selectedTableIds]);

  // Generate combinations from selected tables
  const combinations = useMemo(() => {
    if (manualSelectedTables.length < 2) return [];
    return generateConsecutiveCombinations(manualSelectedTables, combinedTables);
  }, [manualSelectedTables, combinedTables]);

  // Pre-select all new combinations when entering preview step
  useEffect(() => {
    if (step === "preview" && combinations.length > 0) {
      const newIndices = new Set<number>();
      combinations.forEach((combo, idx) => {
        if (!combo.alreadyExists) {
          newIndices.add(idx);
        }
      });
      setSelectedCombinations(newIndices);
    }
  }, [step, combinations]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setStep("selectTables");
      setSelectedTableIds(new Set());
      setIsCreating(false);
      setSelectedCombinations(new Set());
      setEffectiveCapacities(new Map());
    }
  }, [open]);

  // Calculate counts
  const existingCount = useMemo(
    () => combinations.filter((c) => c.alreadyExists).length,
    [combinations]
  );
  const newCount = useMemo(
    () => combinations.filter((c) => !c.alreadyExists).length,
    [combinations]
  );
  const selectedCount = selectedCombinations.size;

  const toggleCombination = useCallback((idx: number) => {
    setSelectedCombinations((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  }, []);

  const toggleTable = useCallback((tableId: string) => {
    setSelectedTableIds((prev) => {
      const next = new Set(prev);
      if (next.has(tableId)) {
        next.delete(tableId);
      } else {
        next.add(tableId);
      }
      return next;
    });
  }, []);

  const selectAllCombinations = useCallback(() => {
    const newIndices = new Set<number>();
    combinations.forEach((combo, idx) => {
      if (!combo.alreadyExists) {
        newIndices.add(idx);
      }
    });
    setSelectedCombinations(newIndices);
  }, [combinations]);

  const deselectAllCombinations = useCallback(() => {
    setSelectedCombinations(new Set());
  }, []);

  const selectAllTables = useCallback(() => {
    setSelectedTableIds(new Set(sortedActiveTables.map((t) => t.id)));
  }, [sortedActiveTables]);

  const deselectAllTables = useCallback(() => {
    setSelectedTableIds(new Set());
  }, []);

  const handleContinue = () => {
    if (manualSelectedTables.length >= 2) {
      setStep("preview");
    }
  };

  const handleBack = () => {
    if (step === "preview") {
      setStep("selectTables");
      setSelectedCombinations(new Set());
      setEffectiveCapacities(new Map());
    }
  };

  const handleEffectiveCapacityChange = useCallback((idx: number, value: string) => {
    const numValue = parseInt(value, 10);
    setEffectiveCapacities((prev) => {
      const next = new Map(prev);
      if (isNaN(numValue) || numValue <= 0) {
        next.delete(idx);
      } else {
        next.set(idx, numValue);
      }
      return next;
    });
  }, []);

  const handleCreate = async () => {
    const selectedToCreate = combinations
      .filter((_, idx) => selectedCombinations.has(idx) && !combinations[idx].alreadyExists)
      .map((combo) => {
        // Find the original index in the combinations array
        const idx = combinations.indexOf(combo);
        const effectiveCap = effectiveCapacities.get(idx);
        const result: TableCombination = {
          ...combo,
          effectiveCapacity: effectiveCap !== undefined ? effectiveCap : combo.totalCapacity,
        };
        return result;
      });
    
    if (selectedToCreate.length === 0) return;

    setIsCreating(true);
    try {
      await onCreateCombinations(selectedToCreate);
      onOpenChange(false);
    } finally {
      setIsCreating(false);
    }
  };

  const getDialogDescription = () => {
    if (step === "selectTables") {
      return "Sélectionnez les tables que vous souhaitez combiner entre elles.";
    }
    return `Sélectionnez les combinaisons à créer (${manualSelectedTables.length} tables sélectionnées)`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5" />
            Générer des combinaisons
          </DialogTitle>
          <DialogDescription>{getDialogDescription()}</DialogDescription>
        </DialogHeader>

        {/* Table Selection Step */}
        {step === "selectTables" && (
          <div className="py-4">
            <div className="flex gap-2 mb-3">
              <Button
                variant="outline"
                size="sm"
                onClick={selectAllTables}
                disabled={selectedTableIds.size === sortedActiveTables.length}
              >
                Tout sélectionner
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={deselectAllTables}
                disabled={selectedTableIds.size === 0}
              >
                Tout désélectionner
              </Button>
            </div>

            <ScrollArea className="h-[280px] pr-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {sortedActiveTables.map((table) => {
                  const isSelected = selectedTableIds.has(table.id);
                  return (
                    <div
                      key={table.id}
                      onClick={() => toggleTable(table.id)}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        isSelected
                          ? "bg-primary/10 border-primary/40"
                          : "bg-background border-border hover:bg-muted/50"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleTable(table.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{table.name}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {table.capacity} places
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            <div className="mt-4 p-3 bg-muted/50 rounded-lg text-sm">
              <div className="flex items-center justify-between">
                <span>Tables sélectionnées :</span>
                <span className="font-medium">{selectedTableIds.size}</span>
              </div>
              {selectedTableIds.size >= 2 && (
                <div className="flex items-center justify-between text-primary mt-1">
                  <span>Combinaisons possibles :</span>
                  <span className="font-medium">{combinations.length}</span>
                </div>
              )}
              {selectedTableIds.size === 1 && (
                <p className="text-xs text-muted-foreground mt-2">
                  Sélectionnez au moins 2 tables pour générer des combinaisons.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Preview Step */}
        {step === "preview" && (
          <div className="py-4">
            <div className="flex gap-2 mb-3">
              <Button
                variant="outline"
                size="sm"
                onClick={selectAllCombinations}
                disabled={selectedCount === newCount}
              >
                Tout sélectionner
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={deselectAllCombinations}
                disabled={selectedCount === 0}
              >
                Tout désélectionner
              </Button>
            </div>

            <ScrollArea className="h-[320px] pr-4">
              <div className="space-y-3">
                {combinations.map((combo, idx) => {
                  const isSelected = selectedCombinations.has(idx);
                  const isExisting = combo.alreadyExists;
                  const effectiveValue = effectiveCapacities.get(idx) ?? combo.totalCapacity;
                  const hasCustomCapacity = effectiveCapacities.has(idx) && effectiveCapacities.get(idx) !== combo.totalCapacity;

                  return (
                      <div
                        key={idx}
                        className={`p-3 border rounded-lg transition-colors overflow-hidden ${
                        isExisting
                          ? "bg-muted/30 border-dashed opacity-60"
                          : isSelected
                          ? "bg-primary/10 border-primary/40"
                          : "bg-background border-border"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {!isExisting && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleCombination(idx)}
                            className="data-[state=checked]:bg-primary data-[state=checked]:border-primary mt-1"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div 
                            className={!isExisting ? "cursor-pointer" : ""}
                            onClick={() => !isExisting && toggleCombination(idx)}
                          >
                            <p className="font-medium text-sm truncate">{combo.name}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {combo.tables.length} tables • Capacité totale: {combo.totalCapacity}p
                            </p>
                          </div>
                          
                          {/* Capacity input for new combinations */}
                          {!isExisting && isSelected && (
                            <div className="mt-2 bg-muted/50 p-2 rounded-md overflow-hidden">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-medium whitespace-nowrap">Capacité:</span>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-9 w-9"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEffectiveCapacityChange(idx, String(Math.max(1, effectiveValue - 1)));
                                  }}
                                  disabled={effectiveValue <= 1}
                                >
                                  <Minus className="h-4 w-4" />
                                </Button>
                                <span className="text-lg font-semibold min-w-[2rem] text-center">{effectiveValue}</span>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-9 w-9"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEffectiveCapacityChange(idx, String(effectiveValue + 1));
                                  }}
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                                <span className="text-xs text-muted-foreground whitespace-nowrap">({combo.totalCapacity} physique)</span>
                                {hasCustomCapacity && (
                                  <Badge variant="outline" className="text-xs ml-auto">
                                    Modifié
                                  </Badge>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex-shrink-0">
                          {isExisting ? (
                            <Badge variant="secondary" className="text-xs">
                              <Check className="w-3 h-3 mr-1" />
                              Existe
                            </Badge>
                          ) : isSelected ? (
                            <Badge className="text-xs bg-primary/10 text-primary border-primary/20">
                              <Users className="w-3 h-3 mr-1" />
                              {effectiveValue}p
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {combo.totalCapacity}p
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            <div className="mt-4 p-3 bg-muted/50 rounded-lg text-sm">
              <div className="flex items-center justify-between">
                <span>Sélectionnées :</span>
                <span className="font-medium">
                  {selectedCount} / {newCount} nouvelle{newCount > 1 ? "s" : ""}
                </span>
              </div>
              {existingCount > 0 && (
                <div className="flex items-center justify-between text-yellow-600 dark:text-yellow-400 mt-1">
                  <span>Existantes (non sélectionnables) :</span>
                  <span>{existingCount}</span>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          {step === "selectTables" ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Annuler
              </Button>
              <Button onClick={handleContinue} disabled={selectedTableIds.size < 2}>
                Voir les combinaisons
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleBack} disabled={isCreating}>
                Retour
              </Button>
              <Button onClick={handleCreate} disabled={selectedCount === 0 || isCreating}>
                {isCreating ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Création...
                  </>
                ) : (
                  `Créer ${selectedCount} combinaison${selectedCount > 1 ? "s" : ""}`
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}